import React, { useMemo } from "react";

import { Col, Row, Stack } from "react-bootstrap";

import classNames from "classnames";

import { ResaleFragment } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { useIsPastReportPage } from "@components/detail_drawer/useIsPastReportPage";
import { useAuthenticationContext } from "@context/authentication";
import styles from "@pages/dashboard/DetailsDrawer.module.scss";
import { Role } from "@roles";
import { canEditDetails } from "@util/resale";

import { IconLink } from "../IconLink";
import { ProtectedComponent } from "../permission_control/ProtectedComponent";
import { DetailCell } from "../resale_submission/DetailCell";

import { FieldFormatter } from "./FieldFormatter";

interface ArtistDetailsProps {
  resale: ResaleFragment;
  setIsEditResale: (isInsertMode: boolean) => void;
}

const ARTIST_DETAILS_FORMATTERS: FieldFormatter<ResaleFragment>[] = [
  { title: "Artist", format: (resale: ResaleFragment) => resale.artist_name || "-" },
  {
    title: "Country",
    format: (resale: ResaleFragment) => {
      const artistCountry = resale.artist_country?.trim() || undefined;
      const provisionalCountry = resale.provisional_artist_country?.trim() || undefined;
      return artistCountry ?? provisionalCountry ?? "-";
    },
  },
  {
    title: "Is the artist living?",
    format: (resale: ResaleFragment) =>
      resale.is_artist_living === null ? "Unknown" : resale.is_artist_living ? "Yes" : "No",
  },
  {
    title: "Year of birth",
    format: (resale: ResaleFragment) => {
      const artistYOB = resale.artist_year_of_birth?.toString() || undefined;
      const provisionalYOB = resale.provisional_artist_yob?.toString() || undefined;
      return artistYOB ?? provisionalYOB ?? "Unknown";
    },
  },
  {
    title: "Year of death",
    format: (resale: ResaleFragment) => {
      const artistYOD = resale.artist_year_of_death?.toString() || undefined;
      const provisionalYOD = resale.provisional_artist_yod?.toString() || undefined;
      return artistYOD ?? provisionalYOD ?? "-";
    },
  },
];

export const ArtistDetailsDrawer = ({ resale, setIsEditResale }: ArtistDetailsProps) => {
  const { auth } = useAuthenticationContext();
  const isPastReport = useIsPastReportPage();

  const canEdit = useMemo(
    () => canEditDetails(resale.payment_status, resale.resale_status, isPastReport),
    [isPastReport, resale.payment_status, resale.resale_status],
  );
  return (
    <Stack gap={4} className="col-md-10">
      <Row className="align-items-center">
        <Col>
          <h5 className={classNames(styles["stack-label"])}>Artist details</h5>
        </Col>
        {/*Permit: only VA Admins able to Edit artist/artwork/resale details and if payment_status is in editablePaymentStatuses list*/}
        {canEdit && (
          <ProtectedComponent requiredGroups={[Role.VisualArtistAdmin]} auth={auth}>
            <Col xs="auto">
              <IconLink
                to="#"
                src={PenWriteEditIcon}
                text="Edit"
                className="me-2"
                onClick={() => setIsEditResale(true)}
              />
            </Col>
          </ProtectedComponent>
        )}
      </Row>
      <Row>
        {resale &&
          ARTIST_DETAILS_FORMATTERS.map(({ title, format }) => (
            <Col md={6} className={styles["detail"]} key={title}>
              <DetailCell title={title} value={format(resale)} />
            </Col>
          ))}
      </Row>
    </Stack>
  );
};
